import React from "react";
import { useNavigate } from "react-router-dom";
import { Heart } from "lucide-react";
import { useBookmarks } from "../context/BookmarksContext";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

/**
 * Reusable heart button for tours and cities.
 *
 *   <BookmarkHeart kind="tour" slug="grand-capital-tour" />
 *
 * - Fills gold + scales when bookmarked
 * - Shows public count next to the heart (social proof)
 * - Prompts anon users to sign in instead of silently failing
 */
export default function BookmarkHeart({
  kind, slug, label = "",
  size = "md",        // "sm" | "md" | "lg"
  className = "",
}) {
  const { isBookmarked, toggle, countFor } = useBookmarks();
  const { user } = useAuth();
  const navigate = useNavigate();
  const active = isBookmarked(kind, slug);
  const n = countFor(kind, slug);

  const pad = size === "lg" ? "h-10 w-10" : size === "sm" ? "h-7 w-7" : "h-9 w-9";
  const icon = size === "lg" ? "h-5 w-5" : size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.info("Sign in to save", {
        description: "Create a free account to favourite tours and cities.",
        action: { label: "Sign in", onClick: () => navigate("/login?next=" + window.location.pathname) },
      });
      return;
    }
    const res = await toggle(kind, slug);
    if (res.ok) {
      toast.success(res.bookmarked ? `Saved ${label || kind}` : `Removed from saved`);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid={`bookmark-${kind}-${slug}`}
      aria-pressed={active}
      aria-label={active ? `Remove ${label || kind} from favourites` : `Save ${label || kind} to favourites`}
      className={`relative inline-flex items-center gap-1.5 ${className}`}
    >
      <span
        className={`${pad} inline-flex items-center justify-center rounded-full border transition-all ${
          active
            ? "bg-gold/15 border-gold text-gold scale-105"
            : "bg-ink/70 border-panel-border text-cream-dim hover:text-gold hover:border-gold/60"
        }`}
      >
        <Heart className={`${icon} ${active ? "fill-gold" : ""}`} strokeWidth={active ? 0 : 2} />
      </span>
      {n > 0 && (
        <span className="text-[10px] font-display tracking-widest text-cream-dim/80">
          {n.toLocaleString()}
        </span>
      )}
    </button>
  );
}
